import React, { useRef, useEffect, useState } from 'react';
import { FingerType, HandType } from '@/types';
import { useFingerPositioning } from '@/hooks/useFingerPositioning';

interface FingerHintsProps {
  currentFinger: FingerType | null;
  nextFinger: FingerType | null;
  isVisible?: boolean;
  isCalibrating?: boolean;
  onCalibrationChange?: (isCalibrating: boolean) => void;
  className?: string;
}

export const FingerHints: React.FC<FingerHintsProps> = ({
  currentFinger,
  nextFinger,
  isVisible = true,
  onCalibrationChange,
  className = ''
}) => {
  const {
    isCalibrating: isPositioningCalibrating,
    updateFingerPosition,
    startCalibration,
    endCalibration,
    resetToDefaults,
    getFingerCircleStyle
  } = useFingerPositioning();

  const leftHandRef = useRef<HTMLDivElement>(null);
  const rightHandRef = useRef<HTMLDivElement>(null);
  const [handDimensions] = useState({
    left: { width: 200, height: 300 },
    right: { width: 200, height: 300 }
  });
  const [dragState, setDragState] = useState<{
    isDragging: boolean;
    finger: FingerType | null;
    offset: { x: number; y: number };
  }>({
    isDragging: false,
    finger: null,
    offset: { x: 0, y: 0 }
  });

  /**
   * Update finger positions when current/next finger changes
   */
  useEffect(() => {
    // This will trigger re-renders with updated active fingers
  }, [currentFinger, nextFinger]);



  /**
   * Handle finger circle drag start
   */
  const handleMouseDown = (e: React.MouseEvent, finger: FingerType) => {
    if (!isPositioningCalibrating) return;

    e.preventDefault();
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const containerRect = (e.target as HTMLElement).offsetParent?.getBoundingClientRect();
    
    if (containerRect) {
      setDragState({
        isDragging: true,
        finger,
        offset: {
          x: e.clientX - rect.left - rect.width / 2,
          y: e.clientY - rect.top - rect.height / 2
        }
      });
    }
  };

  /**
   * Handle mouse move during drag
   */
  const handleMouseMove = (e: React.MouseEvent, hand: HandType) => {
    if (!dragState.isDragging || !dragState.finger) return;

    const container = hand === 'left' ? leftHandRef.current : rightHandRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left - dragState.offset.x;
    const y = e.clientY - rect.top - dragState.offset.y;

    // Convert to relative coordinates
    const relativeX = Math.max(0, Math.min(1, x / rect.width));
    const relativeY = Math.max(0, Math.min(1, y / rect.height));

    updateFingerPosition(dragState.finger, relativeX, relativeY);
  };

  /**
   * Handle mouse up (end drag)
   */
  const handleMouseUp = () => {
    setDragState({
      isDragging: false,
      finger: null,
      offset: { x: 0, y: 0 }
    });
  };

  /**
   * Global mouse event handlers
   */
  useEffect(() => {
    if (dragState.isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        if (!dragState.finger) return;
        
        const hand = dragState.finger.startsWith('left') ? 'left' : 'right';
        const container = hand === 'left' ? leftHandRef.current : rightHandRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const x = e.clientX - rect.left - dragState.offset.x;
        const y = e.clientY - rect.top - dragState.offset.y;

        const relativeX = Math.max(0, Math.min(1, x / rect.width));
        const relativeY = Math.max(0, Math.min(1, y / rect.height));

        updateFingerPosition(dragState.finger, relativeX, relativeY);
      };

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState, updateFingerPosition]);

  /**
   * Render finger circles for a hand
   */
  const renderFingerCircles = (hand: HandType) => {
    const handFingers = hand === 'left' 
      ? ['leftPinky', 'leftRing', 'leftMiddle', 'leftPointing', 'leftThumb'] as FingerType[]
      : ['rightThumb', 'rightPointing', 'rightMiddle', 'rightRing', 'rightPinky'] as FingerType[];

    const dimensions = handDimensions[hand];

    return handFingers.map(finger => {
      const isActive = currentFinger === finger;
      const isNext = nextFinger === finger;
      const circleSize = isActive ? 24 : isNext ? 20 : 16;

      return (
        <div
          key={finger}
          className={`finger-circle ${isActive ? 'active' : ''} ${isNext ? 'next' : ''} ${isPositioningCalibrating ? 'calibrating' : ''}`}
          style={getFingerCircleStyle(finger, dimensions.width, dimensions.height, circleSize)}
          onMouseDown={(e) => handleMouseDown(e, finger)}
          title={`${finger} ${isActive ? '(Current)' : isNext ? '(Next)' : ''}`}
        >
          {isPositioningCalibrating && (
            <div className="finger-label">
              {finger.replace('left', '').replace('right', '')}
            </div>
          )}
        </div>
      );
    });
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`finger-hints ${className}`}>
      {/* Calibration Controls - Hidden but functional */}
      {onCalibrationChange && (
        <div className="calibration-controls" style={{ display: 'none' }}>
          <button
            onClick={() => {
              if (isPositioningCalibrating) {
                endCalibration();
                onCalibrationChange(false);
              } else {
                startCalibration();
                onCalibrationChange(true);
              }
            }}
            className={`calibration-btn ${isPositioningCalibrating ? 'active' : ''}`}
          >
            {isPositioningCalibrating ? '✅ Done' : '⚙️ Calibrate'}
          </button>
          
          {isPositioningCalibrating && (
            <button
              onClick={resetToDefaults}
              className="reset-btn"
            >
              🔄 Reset
            </button>
          )}
        </div>
      )}

      {/* Hands Container */}
      <div className="hands-container">
        {/* Left Hand */}
        <div className="hand-container left">
          <div className="hand-label">Left Hand</div>
          <div 
            ref={leftHandRef}
            className="hand-area left"
            style={{ 
              width: handDimensions.left.width, 
              height: handDimensions.left.height 
            }}
            onMouseMove={(e) => handleMouseMove(e, 'left')}
          >
            {/* Hand image placeholder or SVG */}
            <div className="hand-outline left">
              <svg 
                width="100%" 
                height="100%" 
                viewBox="0 0 200 300" 
                className="hand-svg"
              >
                {/* Left hand outline */}
                <path
                  d="M50 250 C 50 240, 45 200, 50 180 C 55 160, 60 140, 70 120 C 80 100, 90 85, 110 75 C 130 65, 150 70, 170 80 C 175 85, 175 90, 170 95 C 165 100, 160 105, 155 110 C 150 120, 145 140, 140 160 C 135 180, 130 200, 125 220 C 120 240, 115 250, 100 260 C 85 270, 65 265, 50 250 Z"
                  fill="rgba(75, 85, 99, 0.3)"
                  stroke="rgba(156, 163, 175, 0.5)"
                  strokeWidth="2"
                />
                {/* Finger guidelines */}
                <line x1="60" y1="80" x2="60" y2="120" stroke="rgba(156, 163, 175, 0.3)" strokeWidth="1" />
                <line x1="80" y1="70" x2="80" y2="110" stroke="rgba(156, 163, 175, 0.3)" strokeWidth="1" />
                <line x1="100" y1="75" x2="100" y2="115" stroke="rgba(156, 163, 175, 0.3)" strokeWidth="1" />
                <line x1="120" y1="80" x2="120" y2="120" stroke="rgba(156, 163, 175, 0.3)" strokeWidth="1" />
                <line x1="150" y1="180" x2="170" y2="160" stroke="rgba(156, 163, 175, 0.3)" strokeWidth="1" />
              </svg>
            </div>
            
            {renderFingerCircles('left')}
          </div>
        </div>

        {/* Right Hand */}
        <div className="hand-container right">
          <div className="hand-label">Right Hand</div>
          <div 
            ref={rightHandRef}
            className="hand-area right"
            style={{ 
              width: handDimensions.right.width, 
              height: handDimensions.right.height 
            }}
            onMouseMove={(e) => handleMouseMove(e, 'right')}
          >
            {/* Hand image placeholder or SVG */}
            <div className="hand-outline right">
              <svg 
                width="100%" 
                height="100%" 
                viewBox="0 0 200 300" 
                className="hand-svg"
              >
                {/* Right hand outline (mirrored) */}
                <path
                  d="M150 250 C 150 240, 155 200, 150 180 C 145 160, 140 140, 130 120 C 120 100, 110 85, 90 75 C 70 65, 50 70, 30 80 C 25 85, 25 90, 30 95 C 35 100, 40 105, 45 110 C 50 120, 55 140, 60 160 C 65 180, 70 200, 75 220 C 80 240, 85 250, 100 260 C 115 270, 135 265, 150 250 Z"
                  fill="rgba(75, 85, 99, 0.3)"
                  stroke="rgba(156, 163, 175, 0.5)"
                  strokeWidth="2"
                />
                {/* Finger guidelines */}
                <line x1="140" y1="80" x2="140" y2="120" stroke="rgba(156, 163, 175, 0.3)" strokeWidth="1" />
                <line x1="120" y1="70" x2="120" y2="110" stroke="rgba(156, 163, 175, 0.3)" strokeWidth="1" />
                <line x1="100" y1="75" x2="100" y2="115" stroke="rgba(156, 163, 175, 0.3)" strokeWidth="1" />
                <line x1="80" y1="80" x2="80" y2="120" stroke="rgba(156, 163, 175, 0.3)" strokeWidth="1" />
                <line x1="50" y1="180" x2="30" y2="160" stroke="rgba(156, 163, 175, 0.3)" strokeWidth="1" />
              </svg>
            </div>
            
            {renderFingerCircles('right')}
          </div>
        </div>
      </div>

      {/* Current Finger Display */}
      {!isPositioningCalibrating && currentFinger && (
        <div className="current-finger-display">
          <span className="current-finger-text">
            {currentFinger.replace('left', 'Left ').replace('right', 'Right ').replace(/([A-Z])/g, ' $1').trim()}
          </span>
        </div>
      )}

      {/* Instructions */}
      {isPositioningCalibrating && (
        <div className="calibration-instructions">
          <p>🎯 Drag the circles to position them over your finger tips</p>
          <p>👀 Look at your hands from above, like you're typing</p>
        </div>
      )}

      <style>{`
        .finger-hints {
          display: flex;
          flex-direction: column;
          gap: 16px;
          user-select: none;
        }

        .calibration-controls {
          display: flex;
          gap: 12px;
          justify-content: center;
          align-items: center;
        }

        .calibration-btn, .reset-btn {
          padding: 8px 16px;
          border-radius: 8px;
          border: none;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease-out;
        }

        .calibration-btn {
          background: rgba(59, 130, 246, 0.8);
          color: white;
        }

        .calibration-btn.active {
          background: rgba(34, 197, 94, 0.8);
        }

        .reset-btn {
          background: rgba(239, 68, 68, 0.8);
          color: white;
        }

        .calibration-btn:hover, .reset-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .hands-container {
          position: relative;
          width: 100%;
          height: 400px;
        }

        .hand-area {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          border: 2px solid rgba(75, 85, 99, 0.3);
          border-radius: 12px;
          background: rgba(17, 24, 39, 0.5);
          backdrop-filter: blur(5px);
        }

        .hand-outline {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }

        .hand-svg {
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
        }

        .finger-circle {
          position: absolute;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s ease-out;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.625rem;
          font-weight: 600;
          color: white;
          z-index: 10;
        }

        .finger-circle:hover {
          transform: scale(1.1) !important;
          z-index: 20;
        }

        .finger-circle.active {
          background: #22c55e !important;
          border-color: #16a34a !important;
          box-shadow: 0 0 16px rgba(34, 197, 94, 0.6) !important;
          animation: activeFingerPulse 1s ease-in-out infinite;
        }

        .finger-circle.next {
          background: #3b82f6 !important;
          border-color: #2563eb !important;
          box-shadow: 0 0 12px rgba(59, 130, 246, 0.5) !important;
          animation: nextFingerGlow 2s ease-in-out infinite;
        }

        .finger-circle.calibrating {
          cursor: move;
          border: 3px solid #facc15;
          box-shadow: 0 0 8px rgba(250, 204, 21, 0.4);
        }

        .finger-label {
          position: absolute;
          top: -20px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 0.5rem;
          background: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 2px 4px;
          border-radius: 4px;
          white-space: nowrap;
        }

        .current-finger-display {
          text-align: center;
          padding: 8px 16px;
          margin-top: 12px;
        }

        .current-finger-text {
          color: #22c55e;
          font-size: 1rem;
          font-weight: 600;
          text-transform: capitalize;
        }

        .calibration-instructions {
          text-align: center;
          padding: 16px;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 8px;
          color: #93c5fd;
        }

        .calibration-instructions p {
          margin: 4px 0;
          font-size: 0.875rem;
        }

        /* Animations */
        @keyframes activeFingerPulse {
          0%, 100% { 
            transform: scale(1);
            box-shadow: 0 0 16px rgba(34, 197, 94, 0.6);
          }
          50% { 
            transform: scale(1.1);
            box-shadow: 0 0 24px rgba(34, 197, 94, 0.8);
          }
        }

        @keyframes nextFingerGlow {
          0%, 100% { 
            opacity: 0.8;
            box-shadow: 0 0 12px rgba(59, 130, 246, 0.5);
          }
          50% { 
            opacity: 1;
            box-shadow: 0 0 18px rgba(59, 130, 246, 0.7);
          }
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .hands-container {
            height: 300px;
          }
          
          .finger-circle {
            font-size: 0.5rem;
          }
        }

        /* Hide during game for better focus */
        .finger-hints.minimal .current-finger-display,
        .finger-hints.minimal .calibration-controls {
          display: none;
        }

        .finger-hints.minimal .hands-container {
          height: 300px;
        }
      `}</style>
    </div>
  );
};

export default FingerHints;
